import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, Package, ShoppingBag, Wallet, Users, BarChart3, Settings,
  Search, Bell, Menu, X, LogOut, Store, Home, Globe, Coins, Boxes, ShieldCheck, Crown,
} from "lucide-react";
import { ChatBubbleLeftRightIcon } from "@heroicons/react/24/outline";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useChat } from "@/context/ChatContext";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { ChatWidget } from "@/components/ChatWidget";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { SellerTrialBanner } from "@/components/seller/SellerTrialBanner";

export const SELLER_NAV = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "products", label: "Products", icon: Package },
  { key: "inventory", label: "Inventory", icon: Boxes },
  { key: "orders", label: "Orders", icon: ShoppingBag },
  { key: "messages", label: "Messages", icon: ChatBubbleLeftRightIcon, badge: "messages" },
  { key: "payments", label: "Payments", icon: Wallet },
  { key: "subscription", label: "Subscription", icon: Crown },
  { key: "customers", label: "Customers", icon: Users },
  { key: "age", label: "Age Verification", icon: ShieldCheck },
  { key: "analytics", label: "Analytics", icon: BarChart3 },
  { key: "settings", label: "Settings", icon: Settings },
];

export const SellerShell = ({ active, onNavigate, search, onSearch, notifications = [], onReadNotifications, title, subtitle, actions, children }) => {
  const navigate = useNavigate();
  const { user, logout, refresh } = useAuth();
  const { lang, setLang, langs } = useI18n();
  const { currency, setCurrency, currencies } = useCurrency();
  const chat = useChat();
  const chatUnread = chat?.unread || 0;
  const [mobileNav, setMobileNav] = useState(false);
  const unread = notifications.filter((n) => !n.read).length;

  const go = (key) => { setMobileNav(false); onNavigate(key); };
  const initials = (user?.business_name || user?.name || "S").trim().charAt(0).toUpperCase();
  const activeLabel = SELLER_NAV.find((n) => n.key === active)?.label;

  const SideContent = () => (
    <div className="flex flex-col h-full">
      <div className="h-16 flex items-center gap-2 px-5 border-b border-border/60 shrink-0">
        <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center text-white font-black text-lg font-display">M</div>
        <div className="min-w-0">
          <p className="font-display font-black text-sm leading-tight truncate">Xurray<span className="text-primary">Mart</span></p>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Seller Center</p>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
        {SELLER_NAV.map((item) => {
          const Icon = item.icon;
          const isActive = active === item.key;
          return (
            <button
              key={item.key}
              data-testid={`seller-nav-${item.key}`}
              onClick={() => go(item.key)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                isActive ? "bg-primary text-white shadow-sm" : "text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
            >
              <Icon className="w-[18px] h-[18px] shrink-0" />
              <span className="flex-1 text-left">{item.label}</span>
              {item.badge === "messages" && chatUnread > 0 && (
                <span data-testid="seller-nav-messages-badge" className={`min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold flex items-center justify-center ${isActive ? "bg-white text-primary" : "bg-primary text-white"}`}>{chatUnread}</span>
              )}
            </button>
          );
        })}
      </nav>
      <div className="p-3 border-t border-border/60 space-y-0.5">
        <button data-testid="seller-nav-storefront" onClick={() => navigate("/")} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
          <Home className="w-[18px] h-[18px]" />View storefront
        </button>
        <button data-testid="seller-logout" onClick={() => { logout(); navigate("/"); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
          <LogOut className="w-[18px] h-[18px]" />Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-muted/30">
      <ImpersonationBanner />
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 bg-card border-r border-border/60 z-30 flex-col">
        <SideContent />
      </aside>

      {/* Mobile drawer */}
      {mobileNav && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileNav(false)} />
          <div className="relative w-64 bg-card h-full shadow-xl"><SideContent /></div>
        </div>
      )}

      <div className="lg:pl-64 flex flex-col min-h-screen">
        {/* Header */}
        <header className="sticky top-0 z-20 h-16 bg-card/95 backdrop-blur border-b border-border/60 flex items-center gap-3 px-4 sm:px-6">
          <Button variant="ghost" size="icon" className="lg:hidden" data-testid="seller-mobile-menu" onClick={() => setMobileNav(true)}><Menu className="w-5 h-5" /></Button>

          <div className="flex-1 max-w-md relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              data-testid="seller-search"
              value={search}
              onChange={(e) => onSearch?.(e.target.value)}
              placeholder="Search products, orders, customers…"
              className="pl-9 h-10 rounded-full bg-muted/50 border-border/60"
            />
          </div>

          <div className="flex items-center gap-1 ml-auto">
            {/* Currency */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="seller-currency" variant="ghost" size="icon" className="rounded-full font-bold text-sm">
                  {currencies.find((c) => c.code === currency)?.symbol || "€"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {currencies.map((c) => (
                  <DropdownMenuItem key={c.code} data-testid={`seller-currency-${c.code}`} onClick={() => setCurrency(c.code)} className={currency === c.code ? "bg-accent/50 font-semibold" : ""}>
                    <span className="mr-2 w-4 text-center font-bold">{c.symbol}</span>{c.code}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Language */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="seller-language" variant="ghost" size="icon" className="rounded-full">
                  <span className="text-lg leading-none">{langs.find((l) => l.code === lang)?.flag || "🌐"}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {langs.map((l) => (
                  <DropdownMenuItem key={l.code} data-testid={`seller-lang-${l.code}`} onClick={() => setLang(l.code)} className={lang === l.code ? "bg-accent/50 font-semibold" : ""}>
                    <span className="mr-2 text-lg leading-none">{l.flag}</span>{l.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Notifications */}
            <DropdownMenu onOpenChange={(o) => { if (o) onReadNotifications?.(); }}>
              <DropdownMenuTrigger asChild>
                <Button data-testid="seller-notifications" variant="ghost" size="icon" className="rounded-full relative">
                  <Bell className="w-5 h-5" />
                  {unread > 0 && <span data-testid="seller-notif-count" className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-destructive text-white text-[10px] font-bold flex items-center justify-center">{unread > 9 ? "9+" : unread}</span>}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuLabel>Notifications</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {notifications.length === 0 && <div className="px-3 py-6 text-center text-sm text-muted-foreground">You're all caught up</div>}
                {notifications.slice(0, 8).map((n) => (
                  <div key={n.notif_id} data-testid={`seller-notif-${n.notif_id}`} className={`px-3 py-2 text-sm border-b border-border/40 ${!n.read ? "bg-primary/5" : ""}`}>
                    <p className="leading-snug">{n.message || n.title || n.body}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{new Date(n.created_at).toLocaleString()}</p>
                  </div>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Profile */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="seller-profile-menu" className="flex items-center gap-2 pl-1.5 pr-2 py-1 rounded-full hover:bg-muted transition-colors">
                  <span className="w-8 h-8 rounded-full bg-primary/15 text-primary font-bold flex items-center justify-center text-sm">{initials}</span>
                  <span className="hidden sm:block text-sm font-medium max-w-[120px] truncate">{user?.business_name || user?.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => go("settings")}><Settings className="w-4 h-4 mr-2" />Settings</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/")}><Store className="w-4 h-4 mr-2" />Storefront</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); navigate("/"); }}><LogOut className="w-4 h-4 mr-2" />Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page body */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">
            <SellerTrialBanner user={user} onRefresh={refresh} />
            {activeLabel && <div className="mb-4"><Breadcrumbs items={[{ label: "Seller" }, { label: activeLabel }]} onHome={() => onNavigate("dashboard")} /></div>}
            {(title || actions) && (
              <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                  {title && <h1 className="font-display font-bold text-2xl sm:text-3xl">{title}</h1>}
                  {subtitle && <p className="text-muted-foreground text-sm mt-0.5">{subtitle}</p>}
                </div>
                {actions}
              </div>
            )}
            <AnimatePresence mode="wait">
              <motion.div key={active}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.22, ease: "easeOut" }}>
                {children}
              </motion.div>
            </AnimatePresence>
          </div>
        </main>
      </div>
      <ChatWidget />
    </div>
  );
};
